import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { ShoppingCart, Plus, Minus, Trash2, RefreshCw, DollarSign, Wallet, CheckCircle2, Users, X, Search, Tag, Gift, ClipboardList } from 'lucide-react';
import PosCheckoutView from '../components/PosCheckoutView';
import { loadPosPrinting, printPosOrder } from '../utils/posPrinter';
import { Lock, KeyRound } from 'lucide-react';

const money = (c) => '$' + (Number(c || 0) / 100).toFixed(2);
const toCents = (d) => Math.round((parseFloat(d) || 0) * 100);
const COMP_LABELS = { player_comp: 'Player Comp', restaurant_discount: 'Restaurant Discount', employee_meal: 'Employee Meal' };
const compLabel = (t) => COMP_LABELS[t] || 'Discount';

export default function PosRegister() {
  const [location, setLocation] = useState(null);
  const [groups, setGroups] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customer, setCustomer] = useState(null); // { name, userId, table, seat } when pulled from a table
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [tenderInput, setTenderInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // Seated-player picker
  const [seatedOpen, setSeatedOpen] = useState(false);
  const [seated, setSeated] = useState([]);
  const [seatedLoading, setSeatedLoading] = useState(false);
  const [seatedQ, setSeatedQ] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupQ, setLookupQ] = useState('');
  // Comp / discount
  const [compOpen, setCompOpen] = useState(false);
  const [compType, setCompType] = useState('player_comp');
  const [compMode, setCompMode] = useState('full');
  const [compValue, setCompValue] = useState('');
  const [compReason, setCompReason] = useState('');
  // Self shift checkout
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [coOpening, setCoOpening] = useState('');
  const [coCounted, setCoCounted] = useState('');
  const [coBusy, setCoBusy] = useState(false);
  // Active cashier (staff PIN gate)
  const [cashier, setCashier] = useState(null); // { staffName, staffNumber, appUserId, token }
  const [locked, setLocked] = useState(true);
  const [printing, setPrinting] = useState(null); // pos_printing settings (printer IP + auto-print flags)
  const [pinEmp, setPinEmp] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const lastActivityRef = useRef(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [locRes, menuRes, printCfg] = await Promise.all([api.get('/pos/location'), api.get('/pos/menu'), loadPosPrinting()]);
      setLocation(locRes.data.location);
      setPrinting(printCfg);
      setGroups(menuRes.data.groups || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load menu'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (locked) return undefined;
    const id = setInterval(() => { if (Date.now() - lastActivityRef.current > 120000) setLocked(true); }, 10000);
    return () => clearInterval(id);
  }, [locked]);

  const taxBps = location?.taxRateBps || 0;
  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const l of cart) {
      const s = l.priceCents * l.qty;
      subtotal += s;
      if (l.taxable) tax += Math.round((s * taxBps) / 10000);
    }
    return { subtotal, tax, total: subtotal + tax };
  }, [cart, taxBps]);

  const addToCart = (it) => setCart((c) => {
    const i = c.findIndex((l) => l.menuItemId === it.id && !l.notes);
    if (i >= 0) { const n = [...c]; n[i] = { ...n[i], qty: n[i].qty + 1 }; return n; }
    return [...c, { menuItemId: it.id, name: it.name, priceCents: it.priceCents, taxable: it.taxable, qty: 1, notes: '' }];
  });
  const setQty = (idx, d) => setCart((c) => c.map((l, i) => i === idx ? { ...l, qty: Math.max(1, l.qty + d) } : l));
  const removeLine = (idx) => setCart((c) => c.filter((_, i) => i !== idx));
  const setNote = (idx, v) => setCart((c) => c.map((l, i) => i === idx ? { ...l, notes: v } : l));
  const clearAll = () => { setCart([]); setCustomerName(''); setCustomer(null); setOrder(null); setTenderInput(''); setDone(null); };

  const openSeated = async () => {
    setSeatedOpen(true); setSeatedLoading(true); setSeatedQ('');
    try { const res = await api.get('/pos/seated'); setSeated(res.data.players || []); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not load seated players'); }
    finally { setSeatedLoading(false); }
  };
  const pickSeated = (p) => {
    setCustomer({ name: p.playerName, userId: p.userId || null, memberNumber: p.memberNumber || null, table: p.tableName, seat: p.seatNumber });
    setCustomerName(p.playerName);
    setSeatedOpen(false);
  };
  const clearCustomer = () => { setCustomer(null); setCustomerName(''); };

  const openLookup = () => { setLookupOpen(true); setLookupQ(''); setLookupResults([]); };
  const runLookup = async (q) => {
    setLookupQ(q);
    if (q.trim().length < 2) { setLookupResults([]); return; }
    setLookupLoading(true);
    try { const res = await api.get(`/pos/players?q=${encodeURIComponent(q.trim())}`); setLookupResults(res.data.players || []); }
    catch (e) { toast.error(e.response?.data?.error || 'Lookup failed'); }
    finally { setLookupLoading(false); }
  };
  const pickLookup = (p) => {
    setCustomer({ name: p.name, userId: p.userId || null, memberNumber: p.memberNumber || null, table: null, seat: null });
    setCustomerName(p.name);
    setLookupOpen(false);
  };

  const filteredSeated = useMemo(() => {
    const q = seatedQ.trim().toLowerCase();
    if (!q) return seated;
    return seated.filter((p) => (p.playerName || '').toLowerCase().includes(q) || String(p.tableName || '').toLowerCase().includes(q) || String(p.memberNumber || '').includes(q));
  }, [seated, seatedQ]);

  const charge = async () => {
    if (!cart.length) return;
    setBusy(true);
    try {
      const res = await api.post('/pos/orders', {
        customerName: customerName || null,
        customerUserId: customer?.userId || null,
        customerTable: customer?.table || null,
        customerSeat: customer?.seat != null ? String(customer.seat) : null,
        staffToken: cashier?.token || null,
        lines: cart.map((l) => ({ menuItemId: l.menuItemId, qty: l.qty, notes: l.notes || null })),
      });
      setOrder(res.data.order);
      setTenderInput((res.data.order.totalCents / 100).toFixed(2));
    } catch (e) {
      const msg = e.response?.data?.error || 'Could not create order';
      if (msg.includes('PIN')) setLocked(true);
      toast.error(msg);
    }
    finally { setBusy(false); }
  };

  // Kitchen ticket + customer receipt straight to the Epson printer once an
  // order is paid (or comp-settled). Fire-and-forget: a print failure never
  // blocks the sale; the cashier gets a loud toast and can reprint from Orders.
  const autoPrint = async (o) => {
    if (!o || !printing?.printerHost || (!printing.kitchenTicket && !printing.customerReceipt)) return;
    let full = o;
    if (!Array.isArray(o.lines)) {
      try { full = (await api.get(`/pos/orders/${o.id}`)).data.order; } catch (_) { /* print what we have */ }
    }
    const r = await printPosOrder(full, printing, location);
    if (!r.ok) toast.error(`Order #${o.orderNumber} did not print. ${r.error} You can reprint it from Orders.`, { duration: 12000 });
  };

  const payCash = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const res = await api.post(`/pos/orders/${order.id}/tenders`, { type: 'cash', tenderedCents: toCents(tenderInput) });
      const o = res.data.order;
      if (o.status === 'preparing' || o.paidAt) {
        setDone({ order: o, changeCents: o.changeCents || 0 });
        autoPrint(o);
        toast.success(`Order #${o.orderNumber} paid — sent to kitchen`);
      } else { setOrder(o); toast(`Partial payment — ${money(o.totalCents - o.tenderedCents)} still due`); }
    } catch (e) { toast.error(e.response?.data?.error || 'Payment failed'); }
    finally { setBusy(false); }
  };

  const payChipWallet = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const res = await api.post(`/pos/orders/${order.id}/tenders`, { type: 'chip_wallet' });
      const o = res.data.order;
      setDone({ order: o, changeCents: 0 });
      autoPrint(o);
      toast.success(`Order #${o.orderNumber} paid from chip wallet \u2014 sent to kitchen`);
    } catch (e) { toast.error(e.response?.data?.error || 'Chip-wallet charge failed'); }
    finally { setBusy(false); }
  };

  const voidOrder = async () => {
    if (!order) return;
    try { await api.post(`/pos/orders/${order.id}/void`, { reason: 'cancelled at register' }); toast('Order voided'); clearAll(); }
    catch (e) { toast.error(e.response?.data?.error || 'Void failed (needs manage permission)'); }
  };

  const verifyPin = async (e) => {
    if (e) e.preventDefault();
    if (!pinEmp || !pinCode) return;
    setPinBusy(true);
    try {
      const res = await api.post('/pos/staff/verify', { employeeNumber: pinEmp.trim(), pin: pinCode.trim() });
      setCashier({ ...res.data.staff, token: res.data.token });
      setLocked(false); setPinEmp(''); setPinCode('');
      lastActivityRef.current = Date.now();
      toast.success(`Signed in: ${res.data.staff.staffName}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Invalid PIN'); setPinCode(''); }
    finally { setPinBusy(false); }
  };
  const lockRegister = () => setLocked(true);
  const bump = () => { lastActivityRef.current = Date.now(); };

  const openComp = () => { setCompType('player_comp'); setCompMode('full'); setCompValue(''); setCompReason(''); setCompOpen(true); };
  const applyComp = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const body = { type: compType, mode: compMode, reason: compReason || null };
      if (compMode === 'percent') body.percent = parseFloat(compValue) || 0;
      if (compMode === 'amount') body.valueCents = toCents(compValue);
      const res = await api.post(`/pos/orders/${order.id}/discount`, body);
      setOrder(res.data.order);
      setTenderInput(((res.data.order.totalCents || 0) / 100).toFixed(2));
      setCompOpen(false);
      toast.success('Comp/discount applied');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not apply'); }
    finally { setBusy(false); }
  };
  const clearComp = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const res = await api.post(`/pos/orders/${order.id}/discount`, { mode: 'clear' });
      setOrder(res.data.order);
      setTenderInput(((res.data.order.totalCents || 0) / 100).toFixed(2));
    } catch (e) { toast.error(e.response?.data?.error || 'Could not clear'); }
    finally { setBusy(false); }
  };
  const settleComped = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const res = await api.post(`/pos/orders/${order.id}/settle-comp`, {});
      const oo = res.data.order;
      setDone({ order: oo, changeCents: 0 });
      autoPrint(oo);
      toast.success(`Order #${oo.orderNumber} comped \u2014 sent to kitchen`);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not settle comp'); }
    finally { setBusy(false); }
  };
  const runCheckout = async (opening, counted) => {
    setCoBusy(true);
    try {
      const key = cashier?.staffNumber || cashier?.appUserId || '';
      const params = new URLSearchParams();
      if (key) params.set('cashierKey', key);
      if (opening !== '') params.set('openingBankCents', String(toCents(opening)));
      if (counted !== '') params.set('countedCashCents', String(toCents(counted)));
      const res = await api.get(`/pos/reports/checkout?${params.toString()}`);
      setCheckout(res.data.checkout);
    } catch (e) { toast.error(e.response?.data?.error || 'Checkout failed'); }
    finally { setCoBusy(false); }
  };
  const openCheckout = async () => { setCheckoutOpen(true); setCheckout(null); await runCheckout(coOpening, coCounted); };
  const closeMyShift = async () => {
    if (coCounted === '') { toast.error('Count your cash first'); return; }
    setCoBusy(true);
    try {
      await api.post('/pos/shift-checkout', { cashierKey: cashier?.staffNumber || cashier?.appUserId || null, staffName: cashier?.staffName || null, openingBankCents: toCents(coOpening), countedCashCents: toCents(coCounted) });
      toast.success('Shift closed & recorded');
      setCheckoutOpen(false);
    } catch (e) { toast.error(e.response?.data?.error || 'Close failed'); }
    finally { setCoBusy(false); }
  };

  if (done) {
    return (
      <div className="p-6 max-w-md mx-auto text-center text-gray-100">
        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold">Order #{done.order.orderNumber} paid</h1>
        <p className="text-gray-400 mt-1">Total {money(done.order.totalCents)}</p>
        {(done.order.customerTable) && <p className="text-gray-400">Deliver to Table {done.order.customerTable}{done.order.customerSeat ? ` · Seat ${done.order.customerSeat}` : ''}</p>}
        {done.changeCents > 0 && <p className="text-3xl font-bold text-amber-400 mt-4">Change due {money(done.changeCents)}</p>}
        <p className="text-emerald-400 mt-4">Sent to the kitchen.</p>
        <button onClick={clearAll} className="mt-6 px-6 py-3 bg-indigo-600 rounded-xl hover:bg-indigo-500 text-lg font-semibold">New order</button>
      </div>
    );
  }

  return (
    <div className="p-4 text-gray-100 flex flex-col lg:flex-row gap-4 h-[calc(100vh-4rem)]" onMouseDown={bump} onKeyDownCapture={bump} onTouchStart={bump}>
      {locked && (
        <div className="fixed inset-0 bg-gray-900/95 flex items-center justify-center z-[60] p-4">
          <form onSubmit={verifyPin} className="bg-gray-800 rounded-2xl w-full max-w-xs p-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-600 rounded-2xl mb-3"><KeyRound className="w-7 h-7" /></div>
            <h2 className="text-lg font-bold mb-1">Register locked</h2>
            <p className="text-gray-400 text-sm mb-4">Enter your staff ID & PIN to ring sales.</p>
            <input value={pinEmp} onChange={(e) => setPinEmp(e.target.value)} inputMode="numeric" autoFocus placeholder="Employee #" className="w-full mb-2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-center" />
            <input value={pinCode} onChange={(e) => setPinCode(e.target.value)} inputMode="numeric" type="password" placeholder="PIN" className="w-full mb-3 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2.5 text-center text-2xl tracking-widest" />
            <button type="submit" disabled={pinBusy || !pinEmp || !pinCode} className="w-full py-2.5 bg-indigo-600 rounded-lg font-semibold hover:bg-indigo-500 disabled:opacity-40">{pinBusy ? 'Checking…' : 'Sign in'}</button>
          </form>
        </div>
      )}
      {/* Menu */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold">Register</h1>
          <div className="flex items-center gap-2">
            {cashier && <span className="text-xs text-gray-400 mr-1 hidden sm:inline">Cashier: <span className="text-gray-200 font-medium">{cashier.staffName}</span></span>}
            <button onClick={lockRegister} className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-lg text-sm hover:bg-gray-600"><Lock className="w-4 h-4" /> Lock</button>
            <button onClick={openCheckout} className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-lg text-sm hover:bg-gray-600"><ClipboardList className="w-4 h-4" /> Checkout</button>
            <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-lg text-sm hover:bg-gray-600"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Menu</button>
          </div>
        </div>
        {groups.map((g) => (
          <div key={g.id || 'other'} className="mb-5">
            <h2 className="text-sm uppercase tracking-wide text-gray-400 mb-2">{g.name}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {g.items.map((it) => (
                <button key={it.id} onClick={() => addToCart(it)} disabled={!it.available}
                  className={`text-left p-3 rounded-xl border ${it.available ? 'bg-gray-800 border-gray-700 hover:border-indigo-500' : 'bg-gray-800/50 border-gray-800 opacity-50 cursor-not-allowed'}`}>
                  <div className="font-medium leading-tight">{it.name}</div>
                  <div className="text-indigo-300 text-sm mt-1">{money(it.priceCents)}</div>
                  {!it.available && <div className="text-xs text-red-400">86'd</div>}
                </button>
              ))}
            </div>
          </div>
        ))}
        {!groups.length && !loading && <div className="text-gray-500">No menu items. Add them under POS Menu.</div>}
      </div>

      {/* Cart / tender */}
      <div className="w-full lg:w-96 bg-gray-800 rounded-xl p-4 flex flex-col">
        <div className="flex items-center gap-2 mb-3"><ShoppingCart className="w-5 h-5" /><span className="font-semibold">Order</span>
          {cart.length > 0 && <button onClick={clearAll} className="ml-auto text-sm text-gray-400 hover:text-red-400">Clear</button>}
        </div>

        {!order ? (
          <>
            <div className="flex-1 overflow-y-auto space-y-2">
              {cart.map((l, idx) => (
                <div key={idx} className="bg-gray-900 rounded-lg p-2">
                  <div className="flex items-center justify-between"><span className="font-medium">{l.name}</span><span>{money(l.priceCents * l.qty)}</span></div>
                  <div className="flex items-center gap-2 mt-1">
                    <button onClick={() => setQty(idx, -1)} className="p-1 bg-gray-700 rounded"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center">{l.qty}</span>
                    <button onClick={() => setQty(idx, 1)} className="p-1 bg-gray-700 rounded"><Plus className="w-3 h-3" /></button>
                    <input value={l.notes} onChange={(e) => setNote(idx, e.target.value)} placeholder="notes" className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm" />
                    <button onClick={() => removeLine(idx)} className="p-1 text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {!cart.length && <div className="text-gray-500 text-sm text-center py-8">Tap items to add</div>}
            </div>

            {/* Customer */}
            <div className="mt-3">
              {customer ? (
                <div className="flex items-center justify-between bg-indigo-900/40 border border-indigo-700 rounded-lg px-3 py-2">
                  <div className="text-sm">
                    <div className="font-medium">{customer.name}</div>
                    <div className="text-indigo-300 text-xs">{customer.table ? `Table ${customer.table}${customer.seat != null ? ' \u00b7 Seat '+customer.seat : ''}` : (customer.memberNumber ? `#${customer.memberNumber}` : 'custom')}{customer.userId ? ' \u00b7 linked' : ''}</div>
                  </div>
                  <button onClick={clearCustomer} className="p-1 text-gray-400 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="space-y-2">
                  <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name (optional)" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
                  <div className="flex gap-2">
                    <button onClick={openLookup} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-700 rounded-lg text-sm hover:bg-gray-600 whitespace-nowrap"><Search className="w-4 h-4" /> Find player</button>
                    <button onClick={openSeated} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-700 rounded-lg text-sm hover:bg-gray-600 whitespace-nowrap"><Users className="w-4 h-4" /> From table</button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3 border-t border-gray-700 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>{money(totals.subtotal)}</span></div>
              <div className="flex justify-between text-gray-400"><span>Tax</span><span>{money(totals.tax)}</span></div>
              <div className="flex justify-between text-lg font-bold"><span>Total</span><span>{money(totals.total)}</span></div>
            </div>
            <button onClick={charge} disabled={!cart.length || busy} className="mt-3 w-full py-3 bg-indigo-600 rounded-xl font-semibold hover:bg-indigo-500 disabled:opacity-40">Charge {money(totals.total)}</button>
          </>
        ) : (
          <>
            <div className="flex-1">
              <div className="text-center py-4">
                <div className="text-gray-400 text-sm">Order #{order.orderNumber}{order.customerTable ? ` · Table ${order.customerTable}${order.customerSeat ? `/${order.customerSeat}` : ''}` : ''}</div>
                <div className="text-3xl font-bold mt-1">{money(order.totalCents)}</div>
                <div className="text-gray-500 text-sm">due</div>
                {order.discountCents ? (
                  <div className="mt-1 text-amber-400 text-sm">{compLabel(order.discountType)} \u2212{money(order.discountCents)}<button onClick={clearComp} className="ml-2 text-gray-500 hover:text-red-400 underline">clear</button></div>
                ) : null}
              </div>
              {order.totalCents > 0 && (<>
              <label className="block text-sm text-gray-400 mb-1">Cash tendered</label>
              <input value={tenderInput} onChange={(e) => setTenderInput(e.target.value)} inputMode="decimal" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-2xl text-center" />
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[order.totalCents, 2000, 5000, 10000].map((amt, i) => (
                  <button key={i} onClick={() => setTenderInput((amt / 100).toFixed(2))} className="py-2 bg-gray-700 rounded-lg text-sm hover:bg-gray-600">{i === 0 ? 'Exact' : money(amt)}</button>
                ))}
              </div>
              </>)}
            </div>
            {order.totalCents === 0 ? (
              <button onClick={settleComped} disabled={busy} className="mt-3 w-full py-3 bg-amber-600 rounded-xl font-semibold hover:bg-amber-500 disabled:opacity-40 flex items-center justify-center gap-2"><Gift className="w-5 h-5" /> Complete (comped)</button>
            ) : (<>
              <button onClick={payCash} disabled={busy} className="mt-3 w-full py-3 bg-emerald-600 rounded-xl font-semibold hover:bg-emerald-500 disabled:opacity-40 flex items-center justify-center gap-2"><DollarSign className="w-5 h-5" /> Take cash</button>
              {order.customerUserId ? (
                <button onClick={payChipWallet} disabled={busy} className="mt-2 w-full py-3 bg-purple-600 rounded-xl font-semibold hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center gap-2"><Wallet className="w-5 h-5" /> Charge chip wallet {money(order.totalCents - order.tenderedCents)}</button>
              ) : (
                <div className="mt-2 text-center text-xs text-gray-500">Attach a linked player to pay from chip wallet</div>
              )}
            </>)}
            <button onClick={openComp} className="mt-2 w-full py-2 text-sm bg-gray-700 rounded-lg hover:bg-gray-600 flex items-center justify-center gap-2"><Tag className="w-4 h-4" /> {order.discountCents ? 'Change comp / discount' : 'Comp / Discount'}</button>
            <button onClick={voidOrder} className="mt-2 w-full py-2 text-sm text-gray-400 hover:text-red-400">Void order</button>
          </>
        )}
      </div>

      {/* Seated-player picker modal */}
      {seatedOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setSeatedOpen(false)}>
          <div className="bg-gray-800 rounded-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> Seated players</h3>
              <div className="flex items-center gap-2">
                <button onClick={openSeated} className="p-1.5 bg-gray-700 rounded hover:bg-gray-600"><RefreshCw className={`w-4 h-4 ${seatedLoading ? 'animate-spin' : ''}`} /></button>
                <button onClick={() => setSeatedOpen(false)} className="p-1.5 bg-gray-700 rounded hover:bg-gray-600"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-3">
              <input autoFocus value={seatedQ} onChange={(e) => setSeatedQ(e.target.value)} placeholder="Search name, table, member #" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
              {seatedLoading && <div className="text-gray-500 text-sm text-center py-8">Loading…</div>}
              {!seatedLoading && filteredSeated.map((p, i) => (
                <button key={i} onClick={() => pickSeated(p)} className="w-full flex items-center justify-between bg-gray-900 hover:bg-gray-700 rounded-lg px-3 py-2 text-left">
                  <div>
                    <div className="font-medium">{p.playerName}</div>
                    <div className="text-xs text-gray-400">Table {p.tableName}{p.seatNumber != null ? ` · Seat ${p.seatNumber}` : ''}{p.memberNumber ? ` · #${p.memberNumber}` : ''}</div>
                  </div>
                  {p.userId && <span className="text-xs px-2 py-0.5 bg-emerald-800 text-emerald-200 rounded">app</span>}
                </button>
              ))}
              {!seatedLoading && !filteredSeated.length && <div className="text-gray-500 text-sm text-center py-8">No seated players found.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Player lookup modal */}
      {lookupOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setLookupOpen(false)}>
          <div className="bg-gray-800 rounded-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="font-semibold flex items-center gap-2"><Search className="w-5 h-5" /> Find player</h3>
              <button onClick={() => setLookupOpen(false)} className="p-1.5 bg-gray-700 rounded hover:bg-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3">
              <input autoFocus value={lookupQ} onChange={(e) => runLookup(e.target.value)} placeholder="Search name or member #" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
              {lookupLoading && <div className="text-gray-500 text-sm text-center py-8">Searching…</div>}
              {!lookupLoading && lookupQ.trim().length < 2 && <div className="text-gray-500 text-sm text-center py-8">Type at least 2 characters.</div>}
              {!lookupLoading && lookupQ.trim().length >= 2 && lookupResults.map((p, i) => (
                <button key={i} onClick={() => pickLookup(p)} className="w-full flex items-center justify-between bg-gray-900 hover:bg-gray-700 rounded-lg px-3 py-2 text-left">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-gray-400">{p.memberNumber ? `#${p.memberNumber}` : 'no member #'}</div>
                  </div>
                  {p.userId && <span className="text-xs px-2 py-0.5 bg-emerald-800 text-emerald-200 rounded">app</span>}
                </button>
              ))}
              {!lookupLoading && lookupQ.trim().length >= 2 && !lookupResults.length && <div className="text-gray-500 text-sm text-center py-8">No players found.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Comp / discount modal */}
      {compOpen && order && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setCompOpen(false)}>
          <div className="bg-gray-800 rounded-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="font-semibold flex items-center gap-2"><Tag className="w-5 h-5" /> Comp / Discount</h3>
              <button onClick={() => setCompOpen(false)} className="p-1.5 bg-gray-700 rounded hover:bg-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Type</label>
                <select value={compType} onChange={(e) => setCompType(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
                  <option value="player_comp">Player Comp (gaming benefit)</option>
                  <option value="restaurant_discount">Restaurant Discount</option>
                  <option value="employee_meal">Employee Meal</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Amount</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['full', 'Comp all'], ['percent', '% off'], ['amount', '$ off']].map(([m, l]) => (
                    <button key={m} onClick={() => setCompMode(m)} className={`py-2 rounded-lg text-sm ${compMode === m ? 'bg-indigo-600' : 'bg-gray-700 hover:bg-gray-600'}`}>{l}</button>
                  ))}
                </div>
                {compMode !== 'full' && (
                  <input value={compValue} onChange={(e) => setCompValue(e.target.value)} inputMode="decimal" placeholder={compMode === 'percent' ? 'e.g. 20' : 'e.g. 5.00'} className="mt-2 w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" />
                )}
              </div>
              <input value={compReason} onChange={(e) => setCompReason(e.target.value)} placeholder="Reason (optional)" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
              <button onClick={applyComp} disabled={busy} className="w-full py-2.5 bg-amber-600 rounded-lg font-semibold hover:bg-amber-500 disabled:opacity-40">Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Self shift checkout modal */}
      {checkoutOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setCheckoutOpen(false)}>
          <div className="bg-gray-800 rounded-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="font-semibold flex items-center gap-2"><ClipboardList className="w-5 h-5" /> My shift checkout</h3>
              <button onClick={() => setCheckoutOpen(false)} className="p-1.5 bg-gray-700 rounded hover:bg-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="flex items-end gap-2 mb-3">
                <div><label className="block text-xs text-gray-400 mb-1">Opening bank ($)</label><input value={coOpening} onChange={(e) => setCoOpening(e.target.value)} inputMode="decimal" placeholder="0.00" className="w-24 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" /></div>
                <div><label className="block text-xs text-gray-400 mb-1">Cash counted ($)</label><input value={coCounted} onChange={(e) => setCoCounted(e.target.value)} inputMode="decimal" placeholder="0.00" className="w-24 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" /></div>
                <button onClick={() => runCheckout(coOpening, coCounted)} disabled={coBusy} className="px-3 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-40">Update</button>
              </div>
              {!checkout ? <div className="text-gray-500 text-center py-8">Loading\u2026</div> : <PosCheckoutView checkout={checkout} />}
              <button onClick={closeMyShift} disabled={coBusy} className="mt-4 w-full py-2.5 bg-emerald-700 rounded-lg font-semibold hover:bg-emerald-600 disabled:opacity-40">Close my shift</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
